import { useCallback, useEffect, useMemo, useState } from "react";

/* ==========================================================
   CONFIG  (addResort.jsx / Resortrooms.jsx wali same API_BASE rakho)
========================================================== */
const API_BASE = "https://bmtvendorvendorfinal.onrender.com";
const RESORT_API = `${API_BASE}/api/resorts`;
const ROOM_API = `${API_BASE}/api/resort-rooms`;
const PLAN_API = `${API_BASE}/api/resort-rate-plans`;
const PRICING_API = `${API_BASE}/api/resort-pricing`;

const MEAL_PLANS = [
  ["ROOM_ONLY", "Room only"],
  ["BREAKFAST", "Breakfast included"],
  ["HALF_BOARD", "Half board (breakfast + dinner)"],
  ["FULL_BOARD", "Full board (all meals)"],
];
const mealLabel = (k) => MEAL_PLANS.find((m) => m[0] === k)?.[1] || k;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const EMPTY_PLAN = {
  displayName: "",
  description: "",
  mealPlan: "ROOM_ONLY",
  basePrice: "",
  extraAdultPrice: 0,
  extraChildPrice: 0,
  gstPercent: 0,
  cancellationPolicy: "",
  refundable: true,
  minStay: 1,
  maxStay: 30,
  isActive: true,
};

/* ==========================================================
   HELPERS
========================================================== */
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("vendorToken") || ""}` });

async function api(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
  });
  let data = {};
  try { data = await res.json(); } catch { /* empty body */ }
  if (res.status === 401 && data.code === "TOKEN_EXPIRED") {
    localStorage.removeItem("vendorToken");
    localStorage.removeItem("vendorUser");
    window.location.href = "/vendor-login";
  }
  if (!res.ok || data.success === false) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
}

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const num = (v, fallback = 0) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? fallback : Number(v));
const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`; // m = 0..11
const todayYmd = () => {
  const t = new Date();
  return ymd(t.getFullYear(), t.getMonth(), t.getDate());
};

/* from..to (inclusive) YYYY-MM-DD list, filtered by weekdays (0=Sun) — UTC-safe */
function dateRange(from, to, days) {
  const out = [];
  const cur = new Date(`${from}T00:00:00.000Z`);
  const end = new Date(`${to}T00:00:00.000Z`);
  while (cur <= end && out.length < 400) {
    if (days.includes(cur.getUTCDay())) out.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

const codeFrom = (text) => text.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "");

function validatePlan(f) {
  if (!f.displayName.trim()) return "Rate plan name is required.";
  if (f.basePrice === "" || num(f.basePrice, -1) < 0) return "Enter a valid base price.";
  if (num(f.extraAdultPrice) < 0 || num(f.extraChildPrice) < 0) return "Extra prices cannot be negative.";
  if (num(f.gstPercent) < 0 || num(f.gstPercent) > 100) return "GST must be between 0 and 100.";
  if (!Number.isInteger(num(f.minStay, 1)) || num(f.minStay, 1) < 1) return "Minimum stay must be a whole number, at least 1.";
  if (!Number.isInteger(num(f.maxStay, 30)) || num(f.maxStay, 30) < 1) return "Maximum stay must be a whole number, at least 1.";
  if (num(f.maxStay, 30) < num(f.minStay, 1)) return "Maximum stay cannot be less than minimum stay.";
  return "";
}

/* ==========================================================
   SMALL UI PIECES
========================================================== */
function Input({ label, value, onChange, type = "text", placeholder, required, hint, ...rest }) {
  return (
    <div className="bmt-field-group">
      <label>{label}{required && " *"}</label>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} {...rest} />
      {hint && <small style={{ color: "#6b7280" }}>{hint}</small>}
    </div>
  );
}

function Notice({ kind, children }) {
  if (!children) return null;
  const bad = kind === "error";
  return (
    <div
      role={bad ? "alert" : "status"}
      style={{
        padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 14,
        background: bad ? "#fef2f2" : "#f0fdf4", color: bad ? "#991b1b" : "#166534",
        border: `1px solid ${bad ? "#fecaca" : "#bbf7d0"}`,
      }}
    >
      {children}
    </div>
  );
}

/* ==========================================================
   MAIN COMPONENT
========================================================== */
export default function ResortPricing() {
  /* ----- selection ----- */
  const [resorts, setResorts] = useState([]);
  const [resortId, setResortId] = useState(() => sessionStorage.getItem("bmtResortPricingResort") || "");
  const [loadingResorts, setLoadingResorts] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");

  /* ----- rate plans ----- */
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [tab, setTab] = useState("plans"); // plans | prices
  const [planView, setPlanView] = useState("list"); // list | form
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);
  const [savingPlan, setSavingPlan] = useState(false);

  /* ----- date-wise pricing ----- */
  const [planId, setPlanId] = useState("");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [priceMap, setPriceMap] = useState({}); // { "2026-10-15": pricingDoc }
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [priceForm, setPriceForm] = useState({
    from: "", to: "", price: "", extraAdultPrice: 0, extraChildPrice: 0,
    minStay: 1, isAvailable: true, notes: "", days: [0, 1, 2, 3, 4, 5, 6],
  });
  const [savingPrice, setSavingPrice] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const setPlanField = (k, v) => setPlanForm((p) => ({ ...p, [k]: v }));
  const setPriceField = (k, v) => setPriceForm((p) => ({ ...p, [k]: v }));

  const selectedPlan = plans.find((p) => p._id === planId);
  const resortName = resorts.find((r) => r._id === resortId)?.name || "";
  const categoryName = categories.find((c) => c._id === categoryId)?.name || "";

  /* ---------- LOAD RESORTS ---------- */
  useEffect(() => {
    (async () => {
      try {
        const data = await api(`${RESORT_API}/my-resorts`);
        const list = Array.isArray(data.resorts) ? data.resorts : [];
        setResorts(list);
        setResortId((cur) => (list.some((r) => r._id === cur) ? cur : list[0]?._id || ""));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingResorts(false);
      }
    })();
  }, []);

  /* ---------- LOAD CATEGORIES ---------- */
  useEffect(() => {
    if (!resortId) { setCategories([]); setCategoryId(""); return; }
    sessionStorage.setItem("bmtResortPricingResort", resortId);
    (async () => {
      try {
        const data = await api(`${ROOM_API}/resort/${resortId}`);
        const list = Array.isArray(data.rooms) ? data.rooms : [];
        setCategories(list);
        setCategoryId((cur) => (list.some((c) => c._id === cur) ? cur : list[0]?._id || ""));
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [resortId]);

  /* ---------- LOAD RATE PLANS ---------- */
  const loadPlans = useCallback(async () => {
    if (!categoryId) { setPlans([]); return; }
    setLoadingPlans(true);
    try {
      const data = await api(`${PLAN_API}/room-category/${categoryId}`);
      const list = data.ratePlans || data.plans || data.data || (Array.isArray(data) ? data : []);
      setPlans(Array.isArray(list) ? list : []);
    } catch (e) {
      setPlans([]);
      setError(e.message);
    } finally {
      setLoadingPlans(false);
    }
  }, [categoryId]);

  useEffect(() => {
    setPlanView("list");
    loadPlans();
  }, [loadPlans]);

  /* keep a valid plan selected for the pricing tab */
  useEffect(() => {
    setPlanId((cur) => (plans.some((p) => p._id === cur) ? cur : plans.find((p) => p.isActive)?._id || plans[0]?._id || ""));
  }, [plans]);

  /* prefill price form with plan defaults when plan changes */
  useEffect(() => {
    if (!selectedPlan) return;
    setPriceForm((p) => ({
      ...p,
      price: "",
      extraAdultPrice: selectedPlan.extraAdultPrice ?? 0,
      extraChildPrice: selectedPlan.extraChildPrice ?? 0,
      minStay: selectedPlan.minStay ?? 1,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  /* ---------- LOAD DATE-WISE PRICES FOR VISIBLE MONTH ---------- */
  const loadPrices = useCallback(async () => {
    if (!planId) { setPriceMap({}); return; }
    setLoadingPrices(true);
    try {
      const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      const data = await api(`${PRICING_API}/rate-plan/${planId}?startDate=${ymd(year, month, 1)}&endDate=${ymd(year, month, last)}`);
      const map = {};
      (data.pricing || []).forEach((p) => { map[String(p.date).slice(0, 10)] = p; });
      setPriceMap(map);
    } catch (e) {
      setPriceMap({});
      setError(e.message);
    } finally {
      setLoadingPrices(false);
    }
  }, [planId, year, month]);

  useEffect(() => { if (tab === "prices") loadPrices(); }, [tab, loadPrices]);

  /* ==========================================================
     RATE PLAN ACTIONS
  ========================================================== */
  const startNewPlan = () => {
    setPlanForm(EMPTY_PLAN);
    setEditingPlanId(null);
    setError("");
    setSuccess("");
    setPlanView("form");
  };

  const startEditPlan = (p) => {
    setPlanForm({
      ...EMPTY_PLAN,
      displayName: p.displayName || "",
      description: p.description || "",
      mealPlan: p.mealPlan || "ROOM_ONLY",
      basePrice: p.basePrice ?? "",
      extraAdultPrice: p.extraAdultPrice ?? 0,
      extraChildPrice: p.extraChildPrice ?? 0,
      gstPercent: p.gstPercent ?? 0,
      cancellationPolicy: p.cancellationPolicy || "",
      refundable: p.refundable !== false,
      minStay: p.minStay ?? 1,
      maxStay: p.maxStay ?? 30,
      isActive: p.isActive !== false,
    });
    setEditingPlanId(p._id);
    setError("");
    setSuccess("");
    setPlanView("form");
  };

  const savePlan = async () => {
    const problem = validatePlan(planForm);
    if (problem) { setError(problem); setSuccess(""); return; }

    setSavingPlan(true);
    setError("");
    setSuccess("");

    const body = {
      displayName: planForm.displayName.trim(),
      description: planForm.description.trim(),
      mealPlan: planForm.mealPlan,
      basePrice: num(planForm.basePrice),
      extraAdultPrice: num(planForm.extraAdultPrice),
      extraChildPrice: num(planForm.extraChildPrice),
      gstPercent: num(planForm.gstPercent),
      cancellationPolicy: planForm.cancellationPolicy,
      refundable: planForm.refundable,
      minStay: num(planForm.minStay, 1),
      maxStay: num(planForm.maxStay, 30),
      isActive: planForm.isActive,
    };

    try {
      if (editingPlanId) {
        await api(`${PLAN_API}/${editingPlanId}`, { method: "PUT", body: JSON.stringify(body) });
      } else {
        await api(PLAN_API, {
          method: "POST",
          body: JSON.stringify({ ...body, name: codeFrom(planForm.displayName), resortId, roomCategoryId: categoryId }),
        });
      }
      await loadPlans();
      setPlanView("list");
      setSuccess(editingPlanId ? "Rate plan updated." : "Rate plan added.");
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingPlan(false);
    }
  };

  const removePlan = async (p) => {
    if (!window.confirm(`Delete rate plan "${p.displayName}"? This cannot be undone.`)) return;
    setError("");
    try {
      await api(`${PLAN_API}/${p._id}`, { method: "DELETE" });
      setSuccess("Rate plan deleted.");
      loadPlans();
    } catch (e) {
      setError(e.message);
    }
  };

  const togglePlanActive = async (p) => {
    setError("");
    try {
      await api(`${PLAN_API}/${p._id}`, { method: "PUT", body: JSON.stringify({ isActive: !p.isActive }) });
      setPlans((list) => list.map((x) => (x._id === p._id ? { ...x, isActive: !p.isActive } : x)));
    } catch (e) {
      setError(e.message);
    }
  };

  const openPricing = (p) => {
    setPlanId(p._id);
    setTab("prices");
    setError("");
    setSuccess("");
  };

  /* ==========================================================
     DATE-WISE PRICE ACTIONS
  ========================================================== */
  const shiftMonth = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };

  const pickDay = (date) => {
    setPriceForm((p) => {
      const single = p.from && p.from === p.to;
      if (single && date >= p.from) return { ...p, to: date };
      return { ...p, from: date, to: date };
    });
    const existing = priceMap[date];
    if (existing && !priceForm.price) setPriceField("price", existing.price);
  };

  const toggleDay = (d) =>
    setPriceForm((p) => ({ ...p, days: p.days.includes(d) ? p.days.filter((x) => x !== d) : [...p.days, d] }));

  const targetDates = useMemo(
    () => (priceForm.from && priceForm.to && priceForm.to >= priceForm.from ? dateRange(priceForm.from, priceForm.to, priceForm.days) : []),
    [priceForm.from, priceForm.to, priceForm.days]
  );

  const savePrices = async () => {
    setError("");
    setSuccess("");

    if (!selectedPlan) return setError("Select a rate plan first.");
    if (!selectedPlan.isActive) return setError("This rate plan is inactive. Activate it before setting prices.");
    if (!priceForm.from || !priceForm.to) return setError("Choose the dates (click a day on the calendar or use the date fields).");
    if (priceForm.to < priceForm.from) return setError("End date cannot be before start date.");
    if (priceForm.price === "" || num(priceForm.price, -1) < 0) return setError("Enter a valid price.");
    if (num(priceForm.extraAdultPrice, -1) < 0 || num(priceForm.extraChildPrice, -1) < 0) return setError("Extra prices cannot be negative.");
    if (!Number.isInteger(num(priceForm.minStay, 1)) || num(priceForm.minStay, 1) < 1) return setError("Minimum stay must be a whole number, at least 1.");
    if (targetDates.length === 0) return setError("No dates match the selected days of the week.");
    if (targetDates.length > 366) return setError("You can update up to 366 dates at a time.");

    setSavingPrice(true);

    let saved = 0;
    const failed = [];

    // Backend ek request me ek date save karta hai, isliye 10-10 ke batch me bhej rahe hain
    for (let i = 0; i < targetDates.length; i += 10) {
      const batch = targetDates.slice(i, i + 10);
      const results = await Promise.allSettled(
        batch.map((date) =>
          api(PRICING_API, {
            method: "POST",
            body: JSON.stringify({
              ratePlanId: planId,
              date,
              price: num(priceForm.price),
              extraAdultPrice: num(priceForm.extraAdultPrice),
              extraChildPrice: num(priceForm.extraChildPrice),
              minStay: num(priceForm.minStay, 1),
              isAvailable: priceForm.isAvailable,
              notes: priceForm.notes.trim(),
            }),
          })
        )
      );
      results.forEach((r, idx) => {
        if (r.status === "fulfilled") saved += 1;
        else failed.push(`${batch[idx]} (${r.reason?.message || "failed"})`);
      });
    }

    await loadPrices();
    setSavingPrice(false);

    if (failed.length === 0) {
      setSuccess(saved === 1 ? "Price saved for 1 date." : `Prices saved for ${saved} dates.`);
    } else {
      if (saved) setSuccess(`Prices saved for ${saved} dates.`);
      setError(`${failed.length} dates failed: ${failed.slice(0, 3).join(", ")}${failed.length > 3 ? "..." : ""}`);
    }
  };

  /* ==========================================================
     CALENDAR GRID
  ========================================================== */
  const calendarCells = useMemo(() => {
    const firstDow = new Date(Date.UTC(year, month, 1)).getUTCDay();
    const total = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const cells = [];
    for (let i = 0; i < firstDow; i += 1) cells.push(null);
    for (let d = 1; d <= total; d += 1) cells.push({ day: d, date: ymd(year, month, d) });
    return cells;
  }, [year, month]);

  /* ==========================================================
     EMPTY STATES
  ========================================================== */
  const header = (
    <div className="bmt-section-intro">
      <div>
        <h2>Rates & Pricing</h2>
        <p>Create rate plans (meal plan, GST, cancellation) for each room category, then set date-wise prices for weekends, holidays and peak season.</p>
      </div>
      {tab === "plans" && planView === "list" && (
        <button className="bmt-primary-btn" onClick={startNewPlan} disabled={!categoryId}>+ Add Rate Plan</button>
      )}
    </div>
  );

  if (!loadingResorts && resorts.length === 0) {
    return (
      <div className="bmt-premium-page">
        {header}
        <Notice kind="error">{error}</Notice>
        <section className="bmt-form-card">
          <div className="bmt-empty-state">
            <h3>Add a resort first</h3>
            <p>Rate plans belong to a room category. Add your resort and room categories from the menu, then come back here.</p>
          </div>
        </section>
      </div>
    );
  }

  /* ==========================================================
     RATE PLAN FORM VIEW
  ========================================================== */
  if (tab === "plans" && planView === "form") {
    return (
      <div className="bmt-premium-page">
        <div className="bmt-section-intro">
          <div>
            <h2>{editingPlanId ? "Edit Rate Plan" : "Add Rate Plan"}</h2>
            <p>{resortName} • <b>{categoryName}</b></p>
          </div>
          <button className="bmt-secondary-btn" onClick={() => { setPlanView("list"); setError(""); }}>Back to list</button>
        </div>

        <Notice kind="error">{error}</Notice>

        <section className="bmt-form-card">
          <div className="bmt-form-card-head"><h3>Plan details</h3></div>
          <div className="bmt-form-grid">
            <Input
              label="Rate plan name" required value={planForm.displayName}
              onChange={(v) => setPlanField("displayName", v)}
              placeholder="e.g. Deluxe with Breakfast"
              hint={!editingPlanId && planForm.displayName ? `Internal code: ${codeFrom(planForm.displayName)}` : undefined}
            />
            <div className="bmt-field-group">
              <label>Meal plan</label>
              <select value={planForm.mealPlan} onChange={(e) => setPlanField("mealPlan", e.target.value)}>
                {MEAL_PLANS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="bmt-field-group">
            <label>Description</label>
            <textarea rows={3} value={planForm.description} onChange={(e) => setPlanField("description", e.target.value)} placeholder="What guests get with this plan" />
          </div>
        </section>

        <section className="bmt-form-card">
          <div className="bmt-form-card-head">
            <h3>Pricing</h3>
            <p>Base price is used on every date that has no date-wise price.</p>
          </div>
          <div className="bmt-form-grid">
            <Input label="Base price per night (₹)" required type="number" min="0" value={planForm.basePrice} onChange={(v) => setPlanField("basePrice", v)} placeholder="e.g. 4500" />
            <Input label="Extra adult per night (₹)" type="number" min="0" value={planForm.extraAdultPrice} onChange={(v) => setPlanField("extraAdultPrice", v)} />
            <Input label="Extra child per night (₹)" type="number" min="0" value={planForm.extraChildPrice} onChange={(v) => setPlanField("extraChildPrice", v)} />
            <Input label="GST (%)" type="number" min="0" max="100" value={planForm.gstPercent} onChange={(v) => setPlanField("gstPercent", v)} />
          </div>
        </section>

        <section className="bmt-form-card">
          <div className="bmt-form-card-head"><h3>Stay rules</h3></div>
          <div className="bmt-form-grid">
            <Input label="Minimum stay (nights)" type="number" min="1" value={planForm.minStay} onChange={(v) => setPlanField("minStay", v)} />
            <Input label="Maximum stay (nights)" type="number" min="1" value={planForm.maxStay} onChange={(v) => setPlanField("maxStay", v)} />
          </div>
        </section>

        <section className="bmt-form-card">
          <div className="bmt-form-card-head"><h3>Cancellation</h3></div>
          <label className={`bmt-check-card ${planForm.refundable ? "selected" : ""}`} style={{ maxWidth: 320, marginBottom: 12 }}>
            <input type="checkbox" checked={planForm.refundable} onChange={(e) => setPlanField("refundable", e.target.checked)} />
            <span>Refundable booking</span>
          </label>
          <div className="bmt-field-group">
            <label>Cancellation policy</label>
            <textarea rows={4} value={planForm.cancellationPolicy} onChange={(e) => setPlanField("cancellationPolicy", e.target.value)} placeholder="Free cancellation up to 48 hours before check-in..." />
          </div>
        </section>

        <section className="bmt-form-card">
          <div className="bmt-form-card-head"><h3>Availability</h3></div>
          <label className={`bmt-check-card ${planForm.isActive ? "selected" : ""}`} style={{ maxWidth: 360 }}>
            <input type="checkbox" checked={planForm.isActive} onChange={(e) => setPlanField("isActive", e.target.checked)} />
            <span>Guests can book this rate plan</span>
          </label>
        </section>

        <div className="bmt-form-actions">
          <button className="bmt-secondary-btn" disabled={savingPlan} onClick={() => { setPlanView("list"); setError(""); }}>Cancel</button>
          <button className="bmt-primary-btn" disabled={savingPlan} onClick={savePlan}>
            {savingPlan ? "Saving..." : editingPlanId ? "Save changes" : "Add rate plan"}
          </button>
        </div>
      </div>
    );
  }

  /* ==========================================================
     MAIN VIEW (selectors + tabs)
  ========================================================== */
  const tabBtn = (id, text) => (
    <button
      type="button"
      className={tab === id ? "bmt-primary-btn" : "bmt-secondary-btn"}
      onClick={() => { setTab(id); setError(""); setSuccess(""); }}
    >
      {text}
    </button>
  );

  return (
    <div className="bmt-premium-page">
      {header}

      <Notice kind="error">{error}</Notice>
      <Notice kind="success">{success}</Notice>

      <section className="bmt-form-card">
        <div className="bmt-form-grid">
          <div className="bmt-field-group">
            <label>Resort</label>
            <select value={resortId} onChange={(e) => { setResortId(e.target.value); setError(""); setSuccess(""); }} disabled={loadingResorts}>
              {resorts.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>
          </div>
          <div className="bmt-field-group">
            <label>Room category</label>
            <select value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setError(""); setSuccess(""); }} disabled={categories.length === 0}>
              {categories.length === 0 && <option value="">No categories</option>}
              {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {tabBtn("plans", "Rate plans")}
          {tabBtn("prices", "Date-wise prices")}
        </div>
      </section>

      {categories.length === 0 && (
        <section className="bmt-form-card">
          <div className="bmt-empty-state">
            <h3>No room categories in {resortName || "this resort"}</h3>
            <p>Create a room category from "Rooms & Categories" first, then add rate plans for it.</p>
          </div>
        </section>
      )}

      {/* ---------------- RATE PLANS TAB ---------------- */}
      {categories.length > 0 && tab === "plans" && (
        <section className="bmt-form-card">
          {loadingPlans ? (
            <p>Loading rate plans...</p>
          ) : plans.length === 0 ? (
            <div className="bmt-empty-state">
              <h3>No rate plans for {categoryName}</h3>
              <p>Add a rate plan such as "Room only" or "With breakfast" to start taking bookings.</p>
              <button className="bmt-primary-btn" onClick={startNewPlan}>Add rate plan</button>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 14 }}>
              {plans.map((p) => (
                <article
                  key={p._id}
                  style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", padding: 14, border: "1px solid #e5e7eb", borderRadius: 10, opacity: p.isActive ? 1 : 0.7 }}
                >
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <strong style={{ fontSize: 16 }}>{p.displayName}</strong>
                    <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                      {[mealLabel(p.mealPlan), p.refundable ? "Refundable" : "Non-refundable", p.gstPercent ? `${p.gstPercent}% GST` : "", `${p.minStay}-${p.maxStay} nights`]
                        .filter(Boolean).join(" • ")}
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                      <b>{money(p.basePrice)}</b> <span style={{ color: "#6b7280" }}>base price per night</span>
                      {(p.extraAdultPrice > 0 || p.extraChildPrice > 0) && (
                        <span style={{ color: "#6b7280" }}> • Extra adult {money(p.extraAdultPrice)}, child {money(p.extraChildPrice)}</span>
                      )}
                    </div>
                    <span style={{ display: "inline-block", marginTop: 6, fontSize: 12, fontWeight: 600, color: p.isActive ? "#15803d" : "#6b7280" }}>
                      {p.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button className="bmt-primary-btn" onClick={() => openPricing(p)} disabled={!p.isActive} title={p.isActive ? "" : "Activate the plan to set prices"}>Set date prices</button>
                    <button className="bmt-secondary-btn" onClick={() => togglePlanActive(p)}>{p.isActive ? "Deactivate" : "Activate"}</button>
                    <button className="bmt-secondary-btn" onClick={() => startEditPlan(p)}>Edit</button>
                    <button className="bmt-secondary-btn" onClick={() => removePlan(p)}>Delete</button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ---------------- DATE-WISE PRICES TAB ---------------- */}
      {categories.length > 0 && tab === "prices" && (
        <>
          {plans.length === 0 ? (
            <section className="bmt-form-card">
              <div className="bmt-empty-state">
                <h3>Add a rate plan first</h3>
                <p>Date-wise prices are set on a rate plan.</p>
                <button className="bmt-primary-btn" onClick={() => { setTab("plans"); startNewPlan(); }}>Add rate plan</button>
              </div>
            </section>
          ) : (
            <>
              <section className="bmt-form-card">
                <div className="bmt-form-grid">
                  <div className="bmt-field-group">
                    <label>Rate plan</label>
                    <select value={planId} onChange={(e) => setPlanId(e.target.value)}>
                      {plans.map((p) => <option key={p._id} value={p._id}>{p.displayName}{p.isActive ? "" : " (inactive)"}</option>)}
                    </select>
                  </div>
                  {selectedPlan && (
                    <div className="bmt-field-group">
                      <label>Base price</label>
                      <div style={{ padding: "10px 0", fontWeight: 600 }}>{money(selectedPlan.basePrice)} <span style={{ color: "#6b7280", fontWeight: 400 }}>per night, used when no date price is set</span></div>
                    </div>
                  )}
                </div>
              </section>

              <section className="bmt-form-card">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 8 }}>
                  <button className="bmt-secondary-btn" onClick={() => shiftMonth(-1)} aria-label="Previous month">‹ Prev</button>
                  <strong style={{ fontSize: 16 }}>{MONTHS[month]} {year}{loadingPrices ? " (loading...)" : ""}</strong>
                  <button className="bmt-secondary-btn" onClick={() => shiftMonth(1)} aria-label="Next month">Next ›</button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 4 }}>
                  {WEEKDAYS.map((d) => (
                    <div key={d} style={{ textAlign: "center", fontSize: 12, fontWeight: 600, color: "#6b7280", padding: 4 }}>{d}</div>
                  ))}
                  {calendarCells.map((c, i) => {
                    if (!c) return <div key={`e${i}`} />;
                    const entry = priceMap[c.date];
                    const inRange = priceForm.from && priceForm.to && c.date >= priceForm.from && c.date <= priceForm.to;
                    const blocked = entry && entry.isAvailable === false;
                    const shown = entry ? entry.price : selectedPlan?.basePrice;
                    return (
                      <button
                        key={c.date}
                        type="button"
                        onClick={() => pickDay(c.date)}
                        aria-label={`${c.date}, ${blocked ? "not available" : money(shown)}`}
                        style={{
                          minHeight: 62, padding: 6, borderRadius: 8, cursor: "pointer", textAlign: "left",
                          display: "flex", flexDirection: "column", justifyContent: "space-between",
                          border: `1px solid ${inRange ? "#15803d" : c.date === todayYmd() ? "#2563eb" : "#e5e7eb"}`,
                          background: inRange ? "#f0fdf4" : blocked ? "#fef2f2" : "#fff",
                          fontFamily: "inherit",
                        }}
                      >
                        <span style={{ fontSize: 12, color: "#6b7280" }}>{c.day}</span>
                        <span style={{ fontSize: 13, fontWeight: entry ? 700 : 400, color: blocked ? "#b91c1c" : entry ? "#111827" : "#9ca3af" }}>
                          {blocked ? "Closed" : money(shown)}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 10 }}>
                  Bold price = date-wise price you set. Grey price = base price. Click a day to select it, then click a later day to select a range.
                </p>
              </section>

              <section className="bmt-form-card">
                <div className="bmt-form-card-head">
                  <h3>Set price for selected dates</h3>
                  <p>{targetDates.length > 0 ? `${targetDates.length} date${targetDates.length > 1 ? "s" : ""} selected` : "Pick dates on the calendar or type them below"}</p>
                </div>

                <div className="bmt-form-grid">
                  <Input label="From date" required type="date" value={priceForm.from} onChange={(v) => setPriceForm((p) => ({ ...p, from: v, to: p.to && p.to >= v ? p.to : v }))} />
                  <Input label="To date" required type="date" min={priceForm.from || undefined} value={priceForm.to} onChange={(v) => setPriceField("to", v)} />
                  <Input label="Price per night (₹)" required type="number" min="0" value={priceForm.price} onChange={(v) => setPriceField("price", v)} placeholder={selectedPlan ? `Base ${selectedPlan.basePrice}` : ""} />
                  <Input label="Extra adult (₹)" type="number" min="0" value={priceForm.extraAdultPrice} onChange={(v) => setPriceField("extraAdultPrice", v)} />
                  <Input label="Extra child (₹)" type="number" min="0" value={priceForm.extraChildPrice} onChange={(v) => setPriceField("extraChildPrice", v)} />
                  <Input label="Minimum stay (nights)" type="number" min="1" value={priceForm.minStay} onChange={(v) => setPriceField("minStay", v)} />
                </div>

                <div className="bmt-field-group">
                  <label>Apply on these days</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {WEEKDAYS.map((d, i) => (
                      <label key={d} className={`bmt-check-card ${priceForm.days.includes(i) ? "selected" : ""}`} style={{ padding: "6px 12px" }}>
                        <input type="checkbox" checked={priceForm.days.includes(i)} onChange={() => toggleDay(i)} />
                        <span>{d}</span>
                      </label>
                    ))}
                    <button type="button" className="bmt-secondary-btn" onClick={() => setPriceField("days", [5, 6])}>Fri-Sat only</button>
                    <button type="button" className="bmt-secondary-btn" onClick={() => setPriceField("days", [0, 1, 2, 3, 4, 5, 6])}>All days</button>
                  </div>
                </div>

                <div className="bmt-field-group">
                  <label>Notes</label>
                  <input value={priceForm.notes} onChange={(e) => setPriceField("notes", e.target.value)} placeholder="e.g. Diwali weekend, New Year special" />
                </div>

                <label className={`bmt-check-card ${priceForm.isAvailable ? "selected" : ""}`} style={{ maxWidth: 380 }}>
                  <input type="checkbox" checked={priceForm.isAvailable} onChange={(e) => setPriceField("isAvailable", e.target.checked)} />
                  <span>Open for booking on these dates</span>
                </label>

                <div className="bmt-form-actions">
                  <button className="bmt-secondary-btn" disabled={savingPrice} onClick={() => setPriceForm((p) => ({ ...p, from: "", to: "", price: "", notes: "" }))}>Clear selection</button>
                  <button className="bmt-primary-btn" disabled={savingPrice} onClick={savePrices}>
                    {savingPrice ? "Saving..." : targetDates.length > 1 ? `Save ${targetDates.length} dates` : "Save price"}
                  </button>
                </div>
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}